import { ref, onMounted, onUnmounted } from 'vue'

export function useScrollReveal(options: IntersectionObserverInit = {}) {
  const elements = ref<HTMLElement[]>([])
  const isVisible = ref<Set<HTMLElement>>(new Set())
  const reduceMotion = ref(false)
  const elementRoots = new WeakMap<HTMLElement, HTMLElement>()
  let observer: IntersectionObserver | undefined
  let mediaQuery: MediaQueryList | undefined
  let handleMotionChange: ((event: MediaQueryListEvent) => void) | undefined

  function reveal(element: HTMLElement) {
    const visible = new Set(isVisible.value)
    visible.add(element)

    const root = elementRoots.get(element)
    const page = element.closest('.app-page') as HTMLElement | null
    if (root) {
      visible.add(root)
    }
    if (page) visible.add(page)

    isVisible.value = visible
  }

  function ensureObserver() {
    if (observer || reduceMotion.value || typeof IntersectionObserver === 'undefined') return

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          reveal(entry.target as HTMLElement)
          observer?.unobserve(entry.target)
        }
      })
    }, {
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.1,
      ...options,
    })
  }

  onMounted(() => {
    mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    reduceMotion.value = mediaQuery.matches

    handleMotionChange = (e: MediaQueryListEvent) => {
      reduceMotion.value = e.matches
      if (e.matches) {
        elements.value.forEach((el) => {
          reveal(el)
          observer?.unobserve(el)
        })
      } else {
        ensureObserver()
        elements.value.forEach((el) => {
          if (!isVisible.value.has(el)) {
            observer?.observe(el)
          }
        })
      }
    }

    mediaQuery.addEventListener('change', handleMotionChange)

    if (reduceMotion.value || typeof IntersectionObserver === 'undefined') {
      elements.value.forEach(reveal)
      return
    }

    ensureObserver()
    elements.value.forEach((el) => observer?.observe(el))

  })

  onUnmounted(() => {
    observer?.disconnect()
    if (mediaQuery && handleMotionChange) {
      mediaQuery.removeEventListener('change', handleMotionChange)
    }
  })

  function observe(el: HTMLElement | null, root?: HTMLElement | null) {
    if (el && !elements.value.includes(el)) {
      elements.value.push(el)
      if (root) {
        elementRoots.set(el, root)
      }

      if (reduceMotion.value || typeof IntersectionObserver === 'undefined') {
        reveal(el)
      } else {
        ensureObserver()
        observer?.observe(el)
      }
    }
  }

  function unobserve(el: HTMLElement) {
    const index = elements.value.indexOf(el)
    if (index > -1) {
      elements.value.splice(index, 1)
      observer?.unobserve(el)
      const visible = new Set(isVisible.value)
      visible.delete(el)
      isVisible.value = visible
      elementRoots.delete(el)
    }
  }

  return {
    observe,
    unobserve,
    isVisible,
    reduceMotion,
  }
}

export function useStaggeredReveal(
  items: any[],
  options: IntersectionObserverInit = {}
) {
  const { observe, isVisible, reduceMotion } = useScrollReveal(options)

  const observedElements = ref<HTMLElement[]>([])

  function registerElement(index: number, el: HTMLElement | null) {
    if (el) {
      observedElements.value[index] = el
      observe(el)
    }
  }

  function isItemVisible(index: number) {
    if (reduceMotion.value) return true
    return isVisible.value.has(observedElements.value[index])
  }

  return {
    registerElement,
    isItemVisible,
    reduceMotion,
  }
}
